// The pinned IELTS question-group taxonomy — the single source of truth the
// blueprint-first PDF importer (Phase 93+) is built on. Each official IELTS
// task type carries: how it is detected from a printed instruction line, what
// shape its answers take, how it is emitted into the builder structure
// (standalone questions vs an embedded-blank block), and the strict JSON
// schema its per-group extraction call must satisfy.
//
// Instruction lines in Cambridge-style papers are rigidly standardized
// ("Complete the notes below. Choose ONE WORD ONLY…", "Do the following
// statements agree with the information…"), so classification is
// deterministic-first: the regexes below resolve the type BEFORE any AI call,
// and the answer key's shape cross-checks it. The model never chooses types.
//
// MIRRORED to supabase/functions/_shared/ai/ielts-taxonomy.ts (byte-identical
// — no imports).

export const BLUEPRINT_SCHEMA_VERSION = 1;

/** Every question-group type the importer can classify. The first 12 equal the
 *  legacy `question_kind` enum values; the rest are registry-only codes
 *  (question_types rows seeded in the phase-93 migration). */
export const IELTS_GROUP_TYPES = [
  'mcq',
  'tfng',
  'ynng',
  'matching_info',
  'matching_headings',
  'matching_features',
  'form_completion',
  'note_completion',
  'table_completion',
  'summary_completion',
  'sentence_completion',
  'short_answer',
  'mcq_multi',
  'matching_sentence_endings',
  'summary_completion_list',
  'flow_chart_completion',
  'flow_chart_completion_list',
  'diagram_label',
  'map_plan_label',
  'classification',
] as const;
export type IeltsGroupType = (typeof IELTS_GROUP_TYPES)[number];

/** The shape every answer in a group must have — the deterministic oracle used
 *  to cross-check a classified type against the printed answer key. */
export type AnswerShape =
  | 'statement_tf' // TRUE / FALSE / NOT GIVEN
  | 'statement_yn' // YES / NO / NOT GIVEN
  | 'letter' // one letter from an A–H option pool
  | 'roman' // one roman numeral from a headings list (i–x)
  | 'letter_set' // TWO/THREE distinct letters from one pool
  | 'words'; // free words within a word limit

export type GroupEmission =
  | { mode: 'questions'; questionKind: string | null }
  | { mode: 'block'; blockKind: string; hasSharedOptions: boolean };

export interface DetectPattern {
  re: RegExp;
  confidence: number;
}

export interface GroupTypeSpec {
  code: IeltsGroupType;
  officialName: string;
  skills: ReadonlyArray<'reading' | 'listening'>;
  emission: GroupEmission;
  /** Instruction-line patterns, strongest first. Tested against a
   *  whitespace-normalized instruction window. */
  detect: ReadonlyArray<DetectPattern>;
  answerShape: AnswerShape;
  /** An option pool (letters/roman labels) must be derivable for this type. */
  needsPool: boolean;
  needsWordLimit: boolean;
  /** Instructions may say "you may use any letter more than once". */
  mayAllowReuse: boolean;
}

const SPECS: ReadonlyArray<GroupTypeSpec> = [
  {
    code: 'tfng',
    officialName: 'Identifying Information (True/False/Not Given)',
    skills: ['reading'],
    emission: { mode: 'questions', questionKind: 'tfng' },
    detect: [
      { re: /do the following statements agree with the information/i, confidence: 0.98 },
      { re: /\bTRUE\b[\s\S]{0,160}\bFALSE\b[\s\S]{0,160}\bNOT\s*GIVEN\b/, confidence: 0.9 },
    ],
    answerShape: 'statement_tf',
    needsPool: false,
    needsWordLimit: false,
    mayAllowReuse: false,
  },
  {
    code: 'ynng',
    officialName: "Identifying Writer's Views (Yes/No/Not Given)",
    skills: ['reading'],
    emission: { mode: 'questions', questionKind: 'ynng' },
    detect: [
      {
        re: /do the following statements (?:agree|reflect)[\s\S]{0,80}(?:views|claims|opinions?)/i,
        confidence: 0.98,
      },
      { re: /\bYES\b[\s\S]{0,160}\bNO\b[\s\S]{0,160}\bNOT\s*GIVEN\b/, confidence: 0.9 },
    ],
    answerShape: 'statement_yn',
    needsPool: false,
    needsWordLimit: false,
    mayAllowReuse: false,
  },
  {
    code: 'mcq',
    officialName: 'Multiple Choice (one answer)',
    skills: ['reading', 'listening'],
    emission: { mode: 'questions', questionKind: 'mcq' },
    detect: [
      { re: /choose the correct (?:letter|answer)\b[\s\S]{0,40}A\W+B\W+C/i, confidence: 0.96 },
      // Listening's three-option form prints "…letter, A, B or C." — the "or"
      // between B and C defeats the \W+ run above (letters are word chars).
      {
        re: /choose the correct (?:letter|answer)\b[\s\S]{0,40}\bA\W+B\W+or\W+C\b/i,
        confidence: 0.96,
      },
    ],
    answerShape: 'letter',
    needsPool: true,
    needsWordLimit: false,
    mayAllowReuse: false,
  },
  {
    code: 'mcq_multi',
    officialName: 'Multiple Choice (choose TWO/THREE letters)',
    skills: ['reading', 'listening'],
    emission: { mode: 'block', blockKind: 'choice_group', hasSharedOptions: true },
    detect: [{ re: /choose\s+(?:two|three)\s+letters/i, confidence: 0.97 }],
    answerShape: 'letter_set',
    needsPool: true,
    needsWordLimit: false,
    mayAllowReuse: false,
  },
  {
    code: 'matching_headings',
    officialName: 'Matching Headings',
    skills: ['reading'],
    emission: { mode: 'block', blockKind: 'matching_box', hasSharedOptions: true },
    detect: [
      { re: /list of headings/i, confidence: 0.97 },
      { re: /choose the correct heading for (?:each|the)/i, confidence: 0.95 },
    ],
    answerShape: 'roman',
    needsPool: true,
    needsWordLimit: false,
    mayAllowReuse: false,
  },
  {
    code: 'matching_info',
    officialName: 'Matching Information',
    skills: ['reading'],
    emission: { mode: 'block', blockKind: 'matching_box', hasSharedOptions: true },
    detect: [
      {
        re: /which (?:section|paragraph) contains the following information/i,
        confidence: 0.97,
      },
    ],
    answerShape: 'letter',
    needsPool: true,
    needsWordLimit: false,
    mayAllowReuse: true,
  },
  {
    code: 'matching_features',
    officialName: 'Matching Features',
    skills: ['reading', 'listening'],
    emission: { mode: 'block', blockKind: 'matching_box', hasSharedOptions: true },
    detect: [
      {
        re: /look at the following [\s\S]{0,160}?list of/i,
        confidence: 0.92,
      },
      { re: /match each [\s\S]{0,60}? (?:with|to) the correct/i, confidence: 0.9 },
      // Listening matching phrasing: "Choose SIX answers from the box and
      // write the correct letter, A–H, next to Questions 21–26." ("Choose TWO
      // letters" stays mcq_multi — that form says "letters", never "answers
      // from the box".)
      { re: /choose [a-z]+ answers? from the (?:box|list)/i, confidence: 0.94 },
      // Generic listening matching tail ("Write the correct letter, A, B or C,
      // next to Questions 18–20."). Low confidence on purpose: map/plan
      // labelling prints the same tail after "Label the map…", and its 0.95
      // detect must keep outranking this one.
      { re: /write the correct letter\b[\s\S]{0,30}next to questions/i, confidence: 0.85 },
    ],
    answerShape: 'letter',
    needsPool: true,
    needsWordLimit: false,
    mayAllowReuse: true,
  },
  {
    code: 'matching_sentence_endings',
    officialName: 'Matching Sentence Endings',
    skills: ['reading'],
    emission: { mode: 'block', blockKind: 'matching_box', hasSharedOptions: true },
    detect: [{ re: /complete each sentence with the correct ending/i, confidence: 0.98 }],
    answerShape: 'letter',
    needsPool: true,
    needsWordLimit: false,
    mayAllowReuse: false,
  },
  {
    // Classify each statement into one of a small set of lettered categories
    // (A/B/C…). Structurally identical to Matching Features — a letter-pick
    // against a shared lettered pool — so it emits the same matching_box block
    // (dropdown per statement + a category reference box shown once). Letters
    // repeat across statements (mayAllowReuse). Confidence sits just above mcq
    // so a "Choose the correct letter, A–C" line still resolves to classify.
    code: 'classification',
    officialName: 'Classification',
    skills: ['reading', 'listening'],
    emission: { mode: 'block', blockKind: 'matching_box', hasSharedOptions: true },
    detect: [
      { re: /\bclassify the following\b/i, confidence: 0.97 },
      {
        re: /\bclassify (?:the|each) [\s\S]{0,60}? as (?:belonging|relating|according) to/i,
        confidence: 0.96,
      },
    ],
    answerShape: 'letter',
    needsPool: true,
    needsWordLimit: false,
    mayAllowReuse: true,
  },
  {
    code: 'sentence_completion',
    officialName: 'Sentence Completion',
    skills: ['reading', 'listening'],
    emission: { mode: 'questions', questionKind: 'sentence_completion' },
    detect: [{ re: /complete the sentences? below/i, confidence: 0.9 }],
    answerShape: 'words',
    needsPool: false,
    needsWordLimit: true,
    mayAllowReuse: false,
  },
  {
    code: 'short_answer',
    officialName: 'Short-answer Questions',
    skills: ['reading', 'listening'],
    emission: { mode: 'questions', questionKind: 'short_answer' },
    detect: [{ re: /answer the questions? below/i, confidence: 0.9 }],
    answerShape: 'words',
    needsPool: false,
    needsWordLimit: true,
    mayAllowReuse: false,
  },
  {
    code: 'note_completion',
    officialName: 'Note Completion',
    skills: ['reading', 'listening'],
    emission: { mode: 'block', blockKind: 'note', hasSharedOptions: false },
    detect: [{ re: /complete the notes? below/i, confidence: 0.95 }],
    answerShape: 'words',
    needsPool: false,
    needsWordLimit: true,
    mayAllowReuse: false,
  },
  {
    code: 'form_completion',
    officialName: 'Form Completion',
    skills: ['listening'],
    emission: { mode: 'block', blockKind: 'note', hasSharedOptions: false },
    detect: [{ re: /complete the form below/i, confidence: 0.95 }],
    answerShape: 'words',
    needsPool: false,
    needsWordLimit: true,
    mayAllowReuse: false,
  },
  {
    code: 'table_completion',
    officialName: 'Table Completion',
    skills: ['reading', 'listening'],
    emission: { mode: 'block', blockKind: 'table', hasSharedOptions: false },
    detect: [{ re: /complete the table below/i, confidence: 0.95 }],
    answerShape: 'words',
    needsPool: false,
    needsWordLimit: true,
    mayAllowReuse: false,
  },
  {
    code: 'summary_completion_list',
    officialName: 'Summary Completion (from a list)',
    skills: ['reading', 'listening'],
    emission: { mode: 'block', blockKind: 'summary', hasSharedOptions: true },
    detect: [
      {
        re: /complete the summary using the list of (?:words|phrases)/i,
        confidence: 0.97,
      },
    ],
    answerShape: 'letter',
    needsPool: true,
    needsWordLimit: false,
    mayAllowReuse: false,
  },
  {
    code: 'summary_completion',
    officialName: 'Summary Completion (words from the text)',
    skills: ['reading', 'listening'],
    emission: { mode: 'block', blockKind: 'summary', hasSharedOptions: false },
    detect: [{ re: /complete the summary below/i, confidence: 0.95 }],
    answerShape: 'words',
    needsPool: false,
    needsWordLimit: true,
    mayAllowReuse: false,
  },
  {
    code: 'flow_chart_completion',
    officialName: 'Flow-chart Completion',
    skills: ['reading', 'listening'],
    emission: { mode: 'block', blockKind: 'flow_chart', hasSharedOptions: false },
    detect: [{ re: /complete the flow[\s-]?chart below/i, confidence: 0.95 }],
    answerShape: 'words',
    needsPool: false,
    needsWordLimit: true,
    mayAllowReuse: false,
  },
  {
    // The word-bank form of the flow chart, exactly as summary_completion_list
    // is to summary_completion: the gaps are filled from a printed A–F list, so
    // the answer is a LETTER, not words from the passage. Without this the
    // "using the list of words" phrasing matched no detect pattern at all, the
    // group fell back to short_answer, and the gaps rendered as free-text boxes
    // whose letter keys ("A (negative)") no student could ever type.
    code: 'flow_chart_completion_list',
    officialName: 'Flow-chart Completion (from a list)',
    skills: ['reading', 'listening'],
    emission: { mode: 'block', blockKind: 'flow_chart', hasSharedOptions: true },
    detect: [
      {
        re: /complete the flow[\s-]?chart using the list of (?:words|phrases)/i,
        confidence: 0.97,
      },
    ],
    answerShape: 'letter',
    needsPool: true,
    needsWordLimit: false,
    mayAllowReuse: false,
  },
  {
    code: 'diagram_label',
    officialName: 'Diagram Label Completion',
    skills: ['reading', 'listening'],
    emission: { mode: 'block', blockKind: 'diagram', hasSharedOptions: false },
    detect: [
      { re: /label the diagram below/i, confidence: 0.95 },
      { re: /label the diagram\b/i, confidence: 0.93 },
    ],
    answerShape: 'words',
    needsPool: false,
    needsWordLimit: true,
    mayAllowReuse: false,
  },
  {
    code: 'map_plan_label',
    officialName: 'Map / Plan Labelling',
    skills: ['listening', 'reading'],
    emission: { mode: 'block', blockKind: 'map', hasSharedOptions: true },
    detect: [
      { re: /label the (?:map|plan) below/i, confidence: 0.95 },
      // "Label the plan of the museum below." — a qualifier between "plan"
      // and "below" defeats the adjacent form above.
      { re: /label the (?:map|plan)\b/i, confidence: 0.93 },
    ],
    answerShape: 'letter',
    needsPool: true,
    needsWordLimit: false,
    mayAllowReuse: false,
  },
];

export const GROUP_TYPE_SPECS: ReadonlyMap<IeltsGroupType, GroupTypeSpec> = new Map(
  SPECS.map((s) => [s.code, s]),
);

export function isIeltsGroupType(raw: string): raw is IeltsGroupType {
  return (IELTS_GROUP_TYPES as readonly string[]).includes(raw);
}

// ---------------------------------------------------------------------------
// Instruction-window classification
// ---------------------------------------------------------------------------

export interface ClassifiedType {
  code: IeltsGroupType;
  confidence: number;
}

function normalizeWindow(window: string): string {
  return window.replace(/\s+/g, ' ').trim();
}

/** All type matches for an instruction window, strongest first. The list
 *  variant of summary completion outranks the plain one by confidence, so
 *  callers can always take the head. */
export function classifyInstruction(window: string): ClassifiedType[] {
  const normalized = normalizeWindow(window);
  const out: ClassifiedType[] = [];
  for (const spec of SPECS) {
    let best = 0;
    for (const { re, confidence } of spec.detect) {
      if (confidence > best && re.test(normalized)) best = confidence;
    }
    if (best > 0) out.push({ code: spec.code, confidence: best });
  }
  out.sort((a, b) => b.confidence - a.confidence);
  return out;
}

const WORD_NUMBERS: ReadonlyMap<string, number> = new Map([
  ['one', 1],
  ['two', 2],
  ['three', 3],
]);

/** Parse a word limit from the instruction window ("ONE WORD ONLY" → 1,
 *  "NO MORE THAN TWO WORDS AND/OR A NUMBER" → 2), or null. */
export function parseWordLimit(window: string): number | null {
  // OCR routinely mangles the "AND/OR" in "…ONE WORD AND/OR A NUMBER" into
  // "ANDIOR", "AND1OR", "AND | OR" etc. (the slash misread as I/l/1/|). Repair it
  // so the bare "N words and/or a number" form below still parses; the anchored
  // \band…or\b only rewrites that exact token, never words like "understand or".
  const normalized = normalizeWindow(window)
    .toLowerCase()
    .replace(/\band\s*[/\\|1il]+\s*or\b/g, 'and/or');
  const noMoreThan = /no more than (one|two|three) words?/.exec(normalized);
  if (noMoreThan?.[1] !== undefined) return WORD_NUMBERS.get(noMoreThan[1]) ?? null;
  const only = /\b(one|two|three) words? (?:only|and\/or a number)/.exec(normalized);
  if (only?.[1] !== undefined) return WORD_NUMBERS.get(only[1]) ?? null;
  if (/\ba number only\b/.test(normalized)) return 1;
  return null;
}

export const ROMAN_LABELS = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x'] as const;

const LETTER_A_CODE = 65; // 'A'

/** Letters A..end (inclusive), e.g. letterRange('F') → A,B,C,D,E,F. */
export function letterRange(end: string): string[] {
  const endCode = end.toUpperCase().charCodeAt(0);
  if (Number.isNaN(endCode) || endCode < LETTER_A_CODE || endCode > LETTER_A_CODE + 25) return [];
  const out: string[] = [];
  for (let c = LETTER_A_CODE; c <= endCode; c += 1) out.push(String.fromCharCode(c));
  return out;
}

export interface OptionPool {
  kind: 'letters' | 'roman';
  labels: string[];
}

// "…the correct letter, A–F…", "…the list of words, A–I…", "Choose TWO
// letters, A–E", "…has seven paragraphs, A–G". The en/em dash or hyphen
// between the two letters is the anchor.
const POOL_RANGE_RE =
  /\b(?:letters?|endings?|words?|phrases?|options?|sections?|paragraphs?|reasons?|people)\s*,?\s+A\s*[–—-]\s*([B-Z])\b/i;

// Enumerated pools print every letter instead of a dash: "the correct
// person, A, B, C or D" / "the correct letter, A, B or C". Runs on the
// whitespace-collapsed window, so every repetition consumes exactly ", X" —
// fixed shape, linear time (security/detect-unsafe-regex clean). Uppercase
// anchor keeps prose "a, b or c" from false-matching; the letter after "or"
// is the pool's end.
const POOL_ENUMERATED_RE = /\bA(?:, [B-Z])+,? or ([B-Z])\b/;

/** Derive the option pool from the instruction window, or null. Matching
 *  headings pools are roman and sized from the headings list itself, so this
 *  returns letters only. */
export function parsePoolFromInstruction(window: string): OptionPool | null {
  const normalized = normalizeWindow(window);
  const range = POOL_RANGE_RE.exec(normalized);
  if (range?.[1] !== undefined) return { kind: 'letters', labels: letterRange(range[1]) };
  const enumerated = POOL_ENUMERATED_RE.exec(normalized);
  if (enumerated?.[1] !== undefined) {
    return { kind: 'letters', labels: letterRange(enumerated[1]) };
  }
  return null;
}

// Matching-headings pools are roman. The instruction window usually embeds the
// List of Headings itself ("i Wildlife saving initiatives…"), and always names
// the paragraph range ("…has six paragraphs, A–F") — which is the QUESTION
// target range, not the option pool, so POOL_RANGE_RE must never size this
// pool. Longest-first alternation so "viii" is not consumed as "v"+"iii".
const ROMAN_HEADING_LINE_RE = /^(viii|vii|vi|iv|iii|ii|ix|xi|x|v|i)[ \t.):]+\S/i;
const ROMAN_RANGE_END_RE = /\bi\s*[–—-]\s*(viii|vii|vi|iv|iii|ii|ix|x|v)\b/i;

/** Derive a ROMAN option pool for matching headings from the instruction
 *  window: (a) count the printed roman heading lines ("i …", "ii …"), taking
 *  the longest consecutive run from "i" (≥3 lines — prose can't fake that);
 *  else (b) read a printed "i–viii" range. Null when neither is present. */
export function parseRomanPoolFromInstruction(window: string): OptionPool | null {
  const found = new Set<string>();
  for (const line of window.split('\n')) {
    const match = ROMAN_HEADING_LINE_RE.exec(line.trim());
    if (match?.[1] !== undefined) found.add(match[1].toLowerCase());
  }
  let run = 0;
  for (const label of ROMAN_LABELS) {
    if (!found.has(label)) break;
    run += 1;
  }
  if (run >= 3) return { kind: 'roman', labels: [...ROMAN_LABELS.slice(0, run)] };
  const range = ROMAN_RANGE_END_RE.exec(normalizeWindow(window));
  const end = range?.[1]?.toLowerCase();
  const endIndex = end === undefined ? -1 : (ROMAN_LABELS as readonly string[]).indexOf(end);
  if (endIndex >= 1) return { kind: 'roman', labels: [...ROMAN_LABELS.slice(0, endIndex + 1)] };
  return null;
}

/** "NB You may use any letter more than once." */
export function parseAllowReuse(window: string): boolean {
  if (/\bNB\b[\s\S]{0,80}more than once/i.test(window)) return true;
  if (/any letter more than once/i.test(window)) return true;
  // "Letters may be used more than once." — the Classification / Matching
  // phrasing without an "NB" lead-in.
  return /letters?\s+(?:may|can)\s+be\s+used\s+more\s+than\s+once/i.test(window);
}

// ---------------------------------------------------------------------------
// The standard printed instruction (last-resort banner)
// ---------------------------------------------------------------------------
//
// The instruction a group shows the candidate always comes from the paper. When
// a draft reaches the exam with none — an older import whose window was
// discarded, a teacher-added range the paper never headed — the candidate is
// shown a task type and nothing else, which is what the phase-123 report
// documented on six groups.
//
// This builds the official sentence for such a group, and it is NOT the model
// writing exam text: every clause is assembled from facts already PARSED OUT OF
// the paper (the classified type, the printed word limit, the printed option
// pool, the printed reuse note). Where a clause needs a fact that could not be
// derived — which paragraphs a matching-headings task covers, which boxes to
// write in — the clause is OMITTED rather than guessed, because an instruction
// that is wrong in detail is worse than one that is merely brief.

export interface StandardInstructionFacts {
  typeCode: IeltsGroupType;
  /** Reading says "from the passage"; listening says "for each answer". */
  skill?: 'reading' | 'listening' | undefined;
  wordLimit?: number | null | undefined;
  optionPool?: OptionPool | undefined;
  allowReuse?: boolean | undefined;
  /** How many letters a choose-N group takes (its child count). */
  selectCount?: number | undefined;
}

const COUNT_WORD: ReadonlyMap<number, string> = new Map([
  [1, 'ONE'],
  [2, 'TWO'],
  [3, 'THREE'],
]);

/** "A–H" for a lettered pool, "i–viii" for a headings pool. Roman numerals are
 *  printed LOWERCASE on the paper and are the values the student's answer is
 *  matched against, so they are never upper-cased. */
function poolPhrase(pool: OptionPool | undefined): string | null {
  const first = pool?.labels.at(0);
  const last = pool?.labels.at(-1);
  if (first === undefined || last === undefined || first === last) return null;
  if (pool?.kind === 'roman') return `${first.toLowerCase()}–${last.toLowerCase()}`;
  return `${first.toUpperCase()}–${last.toUpperCase()}`;
}

/** "A, B, C or D" — the enumerated form the official paper prints for a short
 *  multiple-choice pool. */
function poolEnumeration(pool: OptionPool | undefined): string | null {
  const labels = (pool?.labels ?? []).map((l) => l.toUpperCase());
  const last = labels.at(-1);
  if (labels.length < 2 || last === undefined) return null;
  return `${labels.slice(0, -1).join(', ')} or ${last}`;
}

/** The word-limit sentence, in the wording of the skill being sat. */
function wordLimitSentence(limit: number, skill: 'reading' | 'listening'): string {
  const source = skill === 'listening' ? '' : ' from the passage';
  if (limit === 1) return `Choose ONE WORD ONLY${source} for each answer.`;
  const word = COUNT_WORD.get(limit);
  if (word === undefined)
    return `Choose no more than ${String(limit)} words${source} for each answer.`;
  return `Choose NO MORE THAN ${word} WORDS${source} for each answer.`;
}

const COMPLETION_TASK: ReadonlyMap<IeltsGroupType, string> = new Map([
  ['note_completion', 'Complete the notes below.'],
  ['form_completion', 'Complete the form below.'],
  ['table_completion', 'Complete the table below.'],
  ['summary_completion', 'Complete the summary below.'],
  ['sentence_completion', 'Complete the sentences below.'],
  ['flow_chart_completion', 'Complete the flow chart below.'],
  ['short_answer', 'Answer the questions below.'],
  ['diagram_label', 'Label the diagram below.'],
  ['map_plan_label', 'Label the map below.'],
]);

/** The official instruction for a group, built only from parsed facts. Empty
 *  when the type is unknown. */
export function standardInstruction(facts: StandardInstructionFacts): string {
  const skill = facts.skill ?? 'reading';
  const range = poolPhrase(facts.optionPool);
  // ", A–F" when the printed pool is known, nothing when it is not — an option
  // range that was never parsed is left unsaid rather than guessed.
  const rangeClause = range === null ? '' : `, ${range}`;
  const sentences: string[] = [];

  switch (facts.typeCode) {
    case 'tfng':
      sentences.push(
        'Do the following statements agree with the information given in the reading passage?',
        'Write TRUE, FALSE or NOT GIVEN.',
      );
      break;
    case 'ynng':
      sentences.push(
        'Do the following statements agree with the claims of the writer in the reading passage?',
        'Write YES, NO or NOT GIVEN.',
      );
      break;
    case 'mcq': {
      const choices = poolEnumeration(facts.optionPool) ?? 'A, B, C or D';
      sentences.push(`Choose the correct letter, ${choices}.`);
      break;
    }
    case 'mcq_multi': {
      const count = COUNT_WORD.get(facts.selectCount ?? 2) ?? 'TWO';
      sentences.push(`Choose ${count} letters${rangeClause}.`);
      break;
    }
    case 'matching_headings':
      // The printed sentence also names WHICH paragraphs the task covers
      // ("for paragraphs B–E"); that range is not derivable here, so it is left
      // out rather than stated wrongly.
      sentences.push(
        'Choose the correct heading for each paragraph from the list of headings below.',
        `Write the correct number, ${poolPhrase(facts.optionPool) ?? 'i–viii'}.`,
      );
      break;
    case 'matching_info':
      sentences.push(
        'Which paragraph contains the following information?',
        `Write the correct letter${rangeClause}.`,
      );
      break;
    case 'matching_features':
      sentences.push(
        `Match each statement with the correct option from the list below${rangeClause}.`,
      );
      break;
    case 'classification':
      sentences.push(
        `Classify each statement under the correct category from the list below${rangeClause}.`,
      );
      break;
    case 'matching_sentence_endings':
      sentences.push(`Complete each sentence with the correct ending${rangeClause}.`);
      break;
    case 'summary_completion_list':
      sentences.push(`Complete the summary using the list of words${rangeClause}, below.`);
      break;
    case 'flow_chart_completion_list':
      sentences.push(`Complete the flow chart using the list of words${rangeClause}, below.`);
      break;
    default: {
      const task = COMPLETION_TASK.get(facts.typeCode);
      if (task === undefined) return '';
      sentences.push(task);
      const limit = facts.wordLimit;
      if (typeof limit === 'number' && limit > 0) sentences.push(wordLimitSentence(limit, skill));
      break;
    }
  }

  if (facts.allowReuse === true) sentences.push('NB You may use any letter more than once.');
  return sentences.join(' ');
}

// A banner that stops on a bare instruction verb lost its object to a hard wrap
// ("…in Reading Passage 1? Write"), which is exactly what the report caught on
// the TRUE/FALSE/NOT GIVEN group.
const TRUNCATED_TAIL_RE = /\b(write|choose|complete|answer|label|match|select)\s*$/i;

/** The clause a truncated banner lost, for the types whose tail is fixed by the
 *  task itself. Null when the missing text cannot be known. */
function missingTail(typeCode: IeltsGroupType): string | null {
  if (typeCode === 'tfng') return 'TRUE, FALSE or NOT GIVEN.';
  if (typeCode === 'ynng') return 'YES, NO or NOT GIVEN.';
  return null;
}

/** Does this banner actually state the task? Empty text and a sentence cut off
 *  mid-clause both fail. */
export function instructionIsComplete(text: string, typeCode: IeltsGroupType): boolean {
  const trimmed = text.trim();
  if (trimmed === '') return false;
  if (TRUNCATED_TAIL_RE.test(trimmed)) return false;
  if (typeCode === 'tfng') return /\bnot\s*given\b/i.test(trimmed);
  if (typeCode === 'ynng') return /\bnot\s*given\b/i.test(trimmed);
  return true;
}

/**
 * The banner to show for a group: the printed instruction when it is whole, the
 * printed instruction with its lost tail restored when a hard wrap cut it, and
 * the standard instruction only when nothing was printed at all. Preferring
 * repair over replacement keeps the paper's own wording (and its passage
 * number) wherever it survived.
 */
export function resolveStandardInstruction(
  printed: string,
  facts: StandardInstructionFacts,
): { text: string; source: 'printed' | 'repaired' | 'standard' } {
  const trimmed = printed.trim();
  if (instructionIsComplete(trimmed, facts.typeCode)) return { text: trimmed, source: 'printed' };
  const tail = trimmed === '' ? null : missingTail(facts.typeCode);
  if (tail !== null && TRUNCATED_TAIL_RE.test(trimmed)) {
    return { text: `${trimmed} ${tail}`, source: 'repaired' };
  }
  const standard = standardInstruction(facts);
  if (standard === '') return { text: trimmed, source: 'printed' };
  return { text: standard, source: 'standard' };
}

// ---------------------------------------------------------------------------
// Answer-shape validation (the manifest × answer-key oracle)
// ---------------------------------------------------------------------------

const TF_TOKENS: ReadonlySet<string> = new Set([
  'true',
  't',
  'false',
  'f',
  'not given',
  'notgiven',
  'ng',
]);
const YN_TOKENS: ReadonlySet<string> = new Set([
  'yes',
  'y',
  'no',
  'n',
  'not given',
  'notgiven',
  'ng',
]);

function normToken(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, ' ');
}

const SINGLE_LETTER_RE = /^[a-z]$/i;
const ROMAN_RE = /^(?:i|ii|iii|iv|v|vi|vii|viii|ix|x)$/i;

// Cambridge prints a choose-TWO key as "A and B" as often as "A, B" or "A/B",
// so the connective is a SEPARATOR, not a letter — without this, "A and B"
// parses as the three-letter set A/AND/B and the whole group fails its shape
// check (which then drops it to 0.3 confidence in preGateBlueprint).
const LETTER_SET_CONNECTIVE: ReadonlySet<string> = new Set(['AND', 'OR', '＆', '+']);

function splitLetterSet(raw: string): string[] {
  return raw
    .split(/[,&/\s]+/)
    .map((s) => s.trim().toUpperCase())
    .filter((s) => s !== '' && !LETTER_SET_CONNECTIVE.has(s));
}

export interface AnswerShapeCheck {
  ok: boolean;
  reason?: string | undefined;
}

function checkLetter(raw: string, pool: readonly string[] | undefined): AnswerShapeCheck {
  const token = raw.trim().toUpperCase();
  if (!SINGLE_LETTER_RE.test(token)) {
    return { ok: false, reason: `“${raw}” is not a single letter` };
  }
  if (pool !== undefined && pool.length > 0 && !pool.includes(token)) {
    return { ok: false, reason: `“${raw}” is outside the option pool A–${pool.at(-1) ?? ''}` };
  }
  return { ok: true };
}

// Official word counting — a byte-equivalent PRIVATE copy of
// countAnswerWords() in answer-key-dsl.ts. Duplicated (not imported) because
// this file must stay import-free for its byte-identical Deno mirror; the
// parity test in answer-key-dsl.test.ts keeps the two implementations honest.
// Rules: hyphenated compound = 1 word; a spelled-out number phrase is ONE
// token ("four hundred years" = 2); figures are one token regardless of digits.

const NUMBER_WORD_TOKENS: ReadonlySet<string> = new Set([
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
  'twenty',
  'thirty',
  'forty',
  'fifty',
  'sixty',
  'seventy',
  'eighty',
  'ninety',
  'hundred',
  'thousand',
  'million',
  'billion',
]);

function numberTokenOf(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Count an answer's words by the official IELTS convention (see the note
 *  above — keep in lockstep with answer-key-dsl.ts's countAnswerWords). */
export function countAnswerTokens(answer: string): number {
  const rawWords = answer.trim().split(/\s+/).filter(Boolean);
  let count = 0;
  let i = 0;
  while (i < rawWords.length) {
    const cleaned = numberTokenOf(rawWords.at(i) ?? '');
    if (NUMBER_WORD_TOKENS.has(cleaned) || /^\d+$/.test(cleaned)) {
      let j = i + 1;
      while (j < rawWords.length && NUMBER_WORD_TOKENS.has(numberTokenOf(rawWords.at(j) ?? ''))) {
        j += 1;
      }
      i = j;
    } else {
      i += 1;
    }
    count += 1;
  }
  return count;
}

function checkWords(raw: string, wordLimit: number | undefined): AnswerShapeCheck {
  const trimmed = raw.trim();
  if (SINGLE_LETTER_RE.test(trimmed)) {
    return { ok: false, reason: `“${raw}” looks like an option letter, not words` };
  }
  if (TF_TOKENS.has(normToken(trimmed)) || YN_TOKENS.has(normToken(trimmed))) {
    return { ok: false, reason: `“${raw}” looks like a statement answer, not words` };
  }
  if (wordLimit !== undefined) {
    // Alternates ("15|fifteen", "colour(s)") each count separately; the limit
    // applies to whichever variant the student would write. Counted with the
    // official tokenizer so a legitimate "four hundred" key never warns.
    const variants = trimmed.split(/[|/]/);
    const withinLimit = variants.some((v) => {
      const cleaned = v.replace(/\((?:the|a|an|s)\)/gi, '').trim();
      const count = countAnswerTokens(cleaned);
      return count > 0 && count <= wordLimit;
    });
    if (!withinLimit) {
      return { ok: false, reason: `“${raw}” exceeds the ${String(wordLimit)}-word limit` };
    }
  }
  return { ok: true };
}

/** Does a printed answer fit the group type's answer shape? The deterministic
 *  gate run over every key BEFORE any extraction call. */
export function validateAnswerForType(
  code: IeltsGroupType,
  raw: string,
  facts: { pool?: readonly string[] | undefined; wordLimit?: number | undefined },
): AnswerShapeCheck {
  const spec = GROUP_TYPE_SPECS.get(code);
  if (spec === undefined) return { ok: false, reason: `unknown type “${code}”` };
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: false, reason: 'empty answer' };
  switch (spec.answerShape) {
    case 'statement_tf':
      return TF_TOKENS.has(normToken(trimmed))
        ? { ok: true }
        : { ok: false, reason: `“${raw}” is not TRUE/FALSE/NOT GIVEN` };
    case 'statement_yn':
      return YN_TOKENS.has(normToken(trimmed))
        ? { ok: true }
        : { ok: false, reason: `“${raw}” is not YES/NO/NOT GIVEN` };
    case 'letter':
      return checkLetter(trimmed, facts.pool);
    case 'roman': {
      const token = trimmed.toLowerCase();
      return ROMAN_RE.test(token)
        ? { ok: true }
        : { ok: false, reason: `“${raw}” is not a roman numeral i–x` };
    }
    case 'letter_set': {
      const letters = splitLetterSet(trimmed);
      if (letters.length < 2 || letters.length > 3) {
        return { ok: false, reason: `“${raw}” is not a set of two or three letters` };
      }
      if (new Set(letters).size !== letters.length) {
        return { ok: false, reason: `“${raw}” repeats a letter` };
      }
      for (const letter of letters) {
        const check = checkLetter(letter, facts.pool);
        if (!check.ok) return check;
      }
      return { ok: true };
    }
    case 'words':
      return checkWords(trimmed, facts.wordLimit);
    default:
      return { ok: false, reason: 'unhandled answer shape' };
  }
}

// ---------------------------------------------------------------------------
// Per-group strict extraction schemas
// ---------------------------------------------------------------------------
//
// Keyed objects, not arrays: OpenAI strict structured outputs guarantee
// `required` + `additionalProperties:false`, so a schema whose properties are
// literally q8..q13 makes the count AND the numbering model-proof.

export interface BlueprintGroupLike {
  first: number;
  last: number;
  typeCode: IeltsGroupType;
  optionPool?: OptionPool | undefined;
}

type JsonSchema = Record<string, unknown>;

function stringProp(): JsonSchema {
  return { type: 'string' };
}

function objectSchema(properties: Record<string, JsonSchema>): JsonSchema {
  return {
    type: 'object',
    additionalProperties: false,
    required: Object.keys(properties),
    properties,
  };
}

function numberKeys(first: number, last: number): string[] {
  const out: string[] = [];
  for (let n = first; n <= last; n += 1) out.push(`q${String(n)}`);
  return out;
}

function keyedStrings(keys: readonly string[]): JsonSchema {
  return objectSchema(Object.fromEntries(keys.map((key) => [key, stringProp()])));
}

function keyedObjects(keys: readonly string[], value: JsonSchema): JsonSchema {
  return objectSchema(Object.fromEntries(keys.map((key) => [key, value])));
}

function poolLabels(group: BlueprintGroupLike, fallbackEnd: string): readonly string[] {
  const labels = group.optionPool?.labels ?? [];
  return labels.length > 0 ? labels : letterRange(fallbackEnd);
}

const NOTE_LAYOUT_SCHEMA: JsonSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['title', 'noteGroups'],
  properties: {
    title: { type: ['string', 'null'] },
    noteGroups: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['heading', 'lines'],
        properties: {
          heading: { type: ['string', 'null'] },
          lines: { type: 'array', items: { type: 'string' } },
        },
      },
    },
  },
};

const TABLE_LAYOUT_SCHEMA: JsonSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['title', 'headers', 'rows'],
  properties: {
    title: { type: ['string', 'null'] },
    headers: { type: 'array', items: { type: 'string' } },
    rows: { type: 'array', items: { type: 'array', items: { type: 'string' } } },
  },
};

const SUMMARY_LAYOUT_SCHEMA: JsonSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['title', 'summaryMd'],
  properties: {
    title: { type: ['string', 'null'] },
    summaryMd: { type: 'string' },
  },
};

/** The strict response schema for one group's extraction call. */
export function extractionSchemaFor(group: BlueprintGroupLike): JsonSchema {
  const spec = GROUP_TYPE_SPECS.get(group.typeCode);
  const keys = numberKeys(group.first, group.last);
  switch (spec?.answerShape) {
    case 'statement_tf':
    case 'statement_yn':
      return objectSchema({ statements: keyedStrings(keys) });
    case 'letter': {
      if (group.typeCode === 'summary_completion_list') {
        return objectSchema({
          layout: SUMMARY_LAYOUT_SCHEMA,
          options: keyedStrings(poolLabels(group, 'H')),
        });
      }
      // The word-bank flow chart keeps the note-shaped layout of its
      // words-from-the-passage sibling and adds the printed A–F list.
      if (group.typeCode === 'flow_chart_completion_list') {
        return objectSchema({
          layout: NOTE_LAYOUT_SCHEMA,
          options: keyedStrings(poolLabels(group, 'H')),
        });
      }
      if (group.typeCode === 'map_plan_label') {
        return objectSchema({
          items: keyedStrings(keys),
          options: keyedStrings(poolLabels(group, 'H')),
        });
      }
      if (group.typeCode === 'mcq') {
        return objectSchema({
          questions: keyedObjects(
            keys,
            objectSchema({ stemMd: stringProp(), choices: keyedStrings(poolLabels(group, 'D')) }),
          ),
        });
      }
      // matching_info's options ARE the passage's paragraph letters — there is
      // no option text to extract, only the numbered statements.
      if (group.typeCode === 'matching_info') {
        return objectSchema({ items: keyedStrings(keys) });
      }
      // matching_features / matching_sentence_endings
      return objectSchema({
        items: keyedStrings(keys),
        options: keyedStrings(poolLabels(group, 'H')),
      });
    }
    case 'roman': {
      const labels = group.optionPool?.labels ?? [...ROMAN_LABELS.slice(0, 8)];
      return objectSchema({ items: keyedStrings(keys), headings: keyedStrings(labels) });
    }
    case 'letter_set':
      return objectSchema({
        stemMd: stringProp(),
        choices: keyedStrings(poolLabels(group, 'E')),
      });
    case 'words': {
      if (group.typeCode === 'sentence_completion' || group.typeCode === 'short_answer') {
        return objectSchema({ sentences: keyedStrings(keys) });
      }
      if (group.typeCode === 'table_completion') {
        return objectSchema({ layout: TABLE_LAYOUT_SCHEMA });
      }
      if (group.typeCode === 'summary_completion') {
        return objectSchema({ layout: SUMMARY_LAYOUT_SCHEMA });
      }
      // note / form / flow-chart / diagram label layouts share the note shape.
      return objectSchema({ layout: NOTE_LAYOUT_SCHEMA });
    }
    default:
      return objectSchema({ items: keyedStrings(keys) });
  }
}
